import React, { useRef, useState } from "react";
import {
  FiFolder,
  FiFileText,
  FiPlus,
  FiCornerDownRight,
  FiCopy,
  FiChevronUp,
  FiChevronDown,
  FiTrash2,
} from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { TemplateSection } from "../../../services/adminTemplatesService";

// --------------------------------------------------------------------------- //
// Builder tree model
// --------------------------------------------------------------------------- //
// A node is a GROUP when it has a `children` array, else a LEAF (a real field).

export interface BuilderNode {
  id: string;
  title: string;
  fieldType: "string" | "array"; // only meaningful for leaves
  children?: BuilderNode[];
}

type Path = number[];

const uid = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const newLeaf = (title = "New Section"): BuilderNode => ({
  id: uid(),
  title,
  fieldType: "string",
});

const isGroup = (n: BuilderNode): boolean => Array.isArray(n.children);

// --------------------------------------------------------------------------- //
// Config <-> builder conversion
// --------------------------------------------------------------------------- //

export const configToBuilder = (sections: TemplateSection[] = []): BuilderNode[] =>
  sections.map((s) => {
    if ("sections" in s && Array.isArray((s as any).sections)) {
      return {
        id: uid(),
        title: s.title,
        fieldType: "string",
        children: configToBuilder((s as any).sections),
      };
    }
    return {
      id: uid(),
      title: s.title,
      fieldType: ((s as any).type as "string" | "array") || "string",
    };
  });

const slugify = (title: string): string =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "section";

export const builderToConfigSections = (nodes: BuilderNode[]): TemplateSection[] => {
  const used = new Set<string>();
  return nodes.map((n) => {
    const base = slugify(n.title);
    let key = base;
    let c = 2;
    while (used.has(key)) key = `${base}_${c++}`;
    used.add(key);

    const title = n.title.trim() || "Section";
    if (n.children && n.children.length > 0) {
      return { key, title, sections: builderToConfigSections(n.children) };
    }
    return { key, title, type: n.fieldType || "string" };
  });
};

// --------------------------------------------------------------------------- //
// Immutable tree helpers (operate by path = array of child indices)
// --------------------------------------------------------------------------- //

const cloneWithNewIds = (node: BuilderNode): BuilderNode => ({
  id: uid(),
  title: node.title,
  fieldType: node.fieldType,
  children: node.children?.map(cloneWithNewIds),
});

// Replace the node at `path`
const updateNode = (
  nodes: BuilderNode[],
  path: Path,
  fn: (n: BuilderNode) => BuilderNode,
): BuilderNode[] => {
  const [head, ...rest] = path;
  return nodes.map((n, i) => {
    if (i !== head) return n;
    if (rest.length === 0) return fn(n);
    return { ...n, children: updateNode(n.children || [], rest, fn) };
  });
};

// Transform the sibling array that contains the node at `path`
const updateSiblings = (
  nodes: BuilderNode[],
  path: Path,
  fn: (arr: BuilderNode[], index: number) => BuilderNode[],
): BuilderNode[] => {
  if (path.length === 1) return fn(nodes, path[0]);
  const [head, ...rest] = path;
  return nodes.map((n, i) =>
    i === head ? { ...n, children: updateSiblings(n.children || [], rest, fn) } : n,
  );
};

// Transform the sibling array at `parentPath` ([] = root)
const updateSiblingArray = (
  nodes: BuilderNode[],
  parentPath: Path,
  fn: (arr: BuilderNode[]) => BuilderNode[],
): BuilderNode[] => {
  if (parentPath.length === 0) return fn(nodes);
  const [head, ...rest] = parentPath;
  return nodes.map((n, i) =>
    i === head ? { ...n, children: updateSiblingArray(n.children || [], rest, fn) } : n,
  );
};

// Any group left with no children reverts to a leaf
const pruneEmptyGroups = (nodes: BuilderNode[]): BuilderNode[] =>
  nodes.map((n) => {
    if (!n.children) return n;
    if (n.children.length === 0) {
      const { children, ...leaf } = n;
      return { ...leaf, fieldType: n.fieldType || "string" };
    }
    return { ...n, children: pruneEmptyGroups(n.children) };
  });

const samePath = (a: Path, b: Path) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //

interface TemplateBuilderProps {
  nodes: BuilderNode[];
  onChange: (nodes: BuilderNode[]) => void;
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({ nodes, onChange }) => {
  const dragPath = useRef<Path | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const addRootSection = () => onChange([...nodes, newLeaf()]);

  const addSiblingAfter = (path: Path) =>
    onChange(
      updateSiblings(nodes, path, (arr, i) => [
        ...arr.slice(0, i + 1),
        newLeaf(),
        ...arr.slice(i + 1),
      ]),
    );

  const addChild = (path: Path) =>
    onChange(
      updateNode(nodes, path, (n) => ({
        ...n,
        children: [...(n.children || []), newLeaf("New Subsection")],
      })),
    );

  const rename = (path: Path, title: string) =>
    onChange(updateNode(nodes, path, (n) => ({ ...n, title })));

  const setFieldType = (path: Path, fieldType: "string" | "array") =>
    onChange(updateNode(nodes, path, (n) => ({ ...n, fieldType })));

  const duplicate = (path: Path) =>
    onChange(
      updateSiblings(nodes, path, (arr, i) => [
        ...arr.slice(0, i + 1),
        cloneWithNewIds(arr[i]),
        ...arr.slice(i + 1),
      ]),
    );

  const remove = (path: Path) =>
    onChange(
      pruneEmptyGroups(
        updateSiblings(nodes, path, (arr, i) => arr.filter((_, j) => j !== i)),
      ),
    );

  const move = (path: Path, dir: -1 | 1) =>
    onChange(
      updateSiblings(nodes, path, (arr, i) => {
        const j = i + dir;
        if (j < 0 || j >= arr.length) return arr;
        const copy = [...arr];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        return copy;
      }),
    );

  // Drag reorder — within the same parent only (buttons handle cross-level)
  const handleDrop = (targetPath: Path) => {
    const source = dragPath.current;
    dragPath.current = null;
    setDragOverId(null);
    if (!source || samePath(source, targetPath)) return;

    const parent = source.slice(0, -1);
    if (!samePath(parent, targetPath.slice(0, -1))) return; // cross-parent: ignore

    const fromIdx = source[source.length - 1];
    const toIdx = targetPath[targetPath.length - 1];
    onChange(
      updateSiblingArray(nodes, parent, (arr) => {
        const copy = [...arr];
        const [moved] = copy.splice(fromIdx, 1);
        const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
        copy.splice(insertAt, 0, moved);
        return copy;
      }),
    );
  };

  const renderNodes = (list: BuilderNode[], parentPath: Path, depth: number) =>
    list.map((node, index) => {
      const path = [...parentPath, index];
      const group = isGroup(node);
      return (
        <div key={node.id}>
          <div
            style={{ marginLeft: depth * 22 }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverId(node.id);
            }}
            onDragLeave={() => setDragOverId((id) => (id === node.id ? null : id))}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(path);
            }}
            className={`group flex items-center gap-2 py-1.5 px-2 rounded-lg border transition ${
              dragOverId === node.id
                ? "border-blue-400 bg-blue-50"
                : "border-transparent hover:bg-gray-50"
            }`}
          >
            <span
              draggable
              onDragStart={() => {
                dragPath.current = path;
              }}
              onDragEnd={() => {
                dragPath.current = null;
                setDragOverId(null);
              }}
              title="Drag to reorder"
              className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
            >
              <MdDragIndicator className="w-4 h-4" />
            </span>

            {group ? (
              <FiFolder className="w-4 h-4 text-blue-500 shrink-0" />
            ) : (
              <FiFileText className="w-4 h-4 text-gray-400 shrink-0" />
            )}

            <input
              value={node.title}
              onChange={(e) => rename(path, e.target.value)}
              placeholder="Section title"
              className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 px-1 py-0.5 rounded
                focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white"
            />

            {!group && (
              <select
                value={node.fieldType}
                onChange={(e) =>
                  setFieldType(path, e.target.value as "string" | "array")
                }
                title="Field type"
                className="text-xs text-gray-500 border border-gray-200 rounded px-1.5 py-0.5
                  focus:outline-none focus:ring-1 focus:ring-blue-400 shrink-0"
              >
                <option value="string">Text</option>
                <option value="array">List</option>
              </select>
            )}

            <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
              <IconBtn title="Add subsection" onClick={() => addChild(path)}>
                <FiCornerDownRight className="w-3.5 h-3.5" />
              </IconBtn>
              <IconBtn title="Add section below" onClick={() => addSiblingAfter(path)}>
                <FiPlus className="w-3.5 h-3.5" />
              </IconBtn>
              <IconBtn title="Duplicate" onClick={() => duplicate(path)}>
                <FiCopy className="w-3.5 h-3.5" />
              </IconBtn>
              <IconBtn title="Move up" onClick={() => move(path, -1)}>
                <FiChevronUp className="w-3.5 h-3.5" />
              </IconBtn>
              <IconBtn title="Move down" onClick={() => move(path, 1)}>
                <FiChevronDown className="w-3.5 h-3.5" />
              </IconBtn>
              <IconBtn title="Delete" danger onClick={() => remove(path)}>
                <FiTrash2 className="w-3.5 h-3.5" />
              </IconBtn>
            </div>
          </div>

          {group && node.children && (
            <div>{renderNodes(node.children, path, depth + 1)}</div>
          )}
        </div>
      );
    });

  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white">
      {nodes.length === 0 ? (
        <div className="text-center py-6 text-sm text-gray-400">
          No sections yet. Add one to start building the document.
        </div>
      ) : (
        <div className="space-y-0.5">{renderNodes(nodes, [], 0)}</div>
      )}
      <button
        type="button"
        onClick={addRootSection}
        className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 px-2 py-1.5 transition"
      >
        <FiPlus className="w-4 h-4" />
        Add Section
      </button>
    </div>
  );
};

const IconBtn: React.FC<{
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`p-1.5 rounded-md transition ${
      danger
        ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
        : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
    }`}
  >
    {children}
  </button>
);

export default TemplateBuilder;
